import { ListItem, ListItemProps, TextVariants } from '@/components';
import TokenIcon from '@/components/organisms/TokenIcon/TokenIcon';
import { useWalletsContext } from '@/components/providers';
import { WalletTokenInterface } from '@/interfaces/WalletTokenInterface';
import {
  formatNumberToAmountInTokenCurrencyString,
  formatNumberToAmountInUsdString,
  formatStringToFirstAndLast4Characters,
} from '@/utils/formatUtils';
import { FC, useMemo } from 'react';
export interface WalletTokenListItemProps
  extends Omit<
    ListItemProps,
    'mainText' | 'subText' | 'subSubText' | 'subSubSubText' | 'subSubSubSubText'
  > {
  token?: WalletTokenInterface;
  tokenSymbol?: string;
  tokenAddress?: string;
  walletAddress?: string;
  align?: 'left' | 'center' | 'right';
  showWalletAddress?: boolean;
  showWalletAddressCopyButton?: boolean;
  showBalance?: boolean;
  showTicker?: boolean;
  showNetwork?: boolean;
  abbreviateWalletAddress?: boolean;
  showWalletAddressPrefix?: boolean;
  showBalancePrefix?: boolean;
  showNetworkPrefix?: boolean;
  showSubNetworkNextToMainNetwork?: boolean;
  mainText?: string;
  subText?: string;
  subSubText?: string;
  subSubSubText?: string;
  subSubSubSubText?: string;
  subSubSubTextSize?: TextVariants['size'];
  subSubSubTextColor?: TextVariants['color'];
  subSubSubSubTextSize?: TextVariants['size'];
  subSubSubSubTextColor?: TextVariants['color'];
  leftColor?: string;
}
const WalletTokenListItem: FC<WalletTokenListItemProps> = ({
  token,
  tokenSymbol,
  tokenAddress,
  walletAddress,
  align = 'left',
  showWalletAddress = true,
  showWalletAddressCopyButton = true,
  showBalance = true,
  showTicker = false,
  showNetwork = false,
  abbreviateWalletAddress = true,
  showWalletAddressPrefix = false,
  showBalancePrefix = false,
  showNetworkPrefix = false,
  showSubNetworkNextToMainNetwork = false,
  mainText,
  subText,
  subSubText,
  subSubSubText,
  subSubSubSubText,
  mainTextSize,
  mainTextColor,
  subTextSize,
  subTextColor,
  subSubTextSize,
  subSubTextColor,
  subSubSubTextSize,
  subSubSubTextColor,
  subSubSubSubTextSize,
  subSubSubSubTextColor,
  leftIconUrl,
  leftIcon,
  rightIconUrl,
  leftColor,
  ...rest
}) => {
  const {
    tokens,
    wallets,
    findWalletByAddressOrToken,
    findTokenBySymbolOrAddress,
  } = useWalletsContext();
  const resolvedToken = useMemo(() => {
    return token || findTokenBySymbolOrAddress(tokenSymbol, tokenAddress);
  }, [token, tokenSymbol, tokenAddress, findTokenBySymbolOrAddress]);
  const resolvedWallet = useMemo(() => {
    return findWalletByAddressOrToken(
      walletAddress,
      resolvedToken || undefined,
    );
  }, [walletAddress, resolvedToken, findWalletByAddressOrToken]);
  if (!resolvedToken || !resolvedWallet) {
    return <div>No token or wallet found</div>;
  }
  const walletAddressDisplay = abbreviateWalletAddress
    ? formatStringToFirstAndLast4Characters(resolvedWallet.address)
    : resolvedWallet.address;
  const formattedWalletAddress = showWalletAddressPrefix
    ? `Wallet ${walletAddressDisplay}`
    : walletAddressDisplay;
  const walletBlockchainDisplayName = resolvedWallet.blockchainDisplayName;
  const tokenBlockchainDisplayName = resolvedToken.blockchainDisplayName;
  const isBlockchainDifferent =
    resolvedToken.blockchain !== resolvedWallet.blockchain;
  const formatBalance = () =>
    `${
      showBalancePrefix ? 'Balance ' : ''
    }${formatNumberToAmountInTokenCurrencyString(
      resolvedToken.balance || 0,
      resolvedToken.symbol || '',
    )} (${formatNumberToAmountInUsdString(resolvedToken.balanceInUsd || 0)})`;
  const formatNetwork = (name: string, isSubNetwork: boolean = false) =>
    showNetworkPrefix
      ? `${isSubNetwork ? 'Sub-network ' : 'Network '}${name}`
      : name;
  let displayedSubText = subText;
  let displayedSubSubText = subSubText;
  let displayedSubSubSubText = subSubSubText;
  let displayedSubSubSubSubText = subSubSubSubText;
  if (showNetwork) {
    if (showWalletAddress) {
      displayedSubText = formattedWalletAddress;
      if (isBlockchainDifferent) {
        if (showSubNetworkNextToMainNetwork) {
          displayedSubSubText = formatNetwork(tokenBlockchainDisplayName);
          displayedSubSubSubText = showBalance ? formatBalance() : undefined;
        } else {
          displayedSubSubText = formatNetwork(walletBlockchainDisplayName);
          displayedSubSubSubText = formatNetwork(
            tokenBlockchainDisplayName,
            true,
          );
          displayedSubSubSubSubText = showBalance ? formatBalance() : undefined;
        }
      } else {
        displayedSubSubText = formatNetwork(walletBlockchainDisplayName);
        displayedSubSubSubText = showBalance ? formatBalance() : undefined;
      }
    } else {
      if (isBlockchainDifferent) {
        if (showSubNetworkNextToMainNetwork) {
          displayedSubText = formatNetwork(tokenBlockchainDisplayName);
          displayedSubSubText = showBalance ? formatBalance() : undefined;
        } else {
          displayedSubText = formatNetwork(walletBlockchainDisplayName);
          displayedSubSubText = formatNetwork(tokenBlockchainDisplayName, true);
          displayedSubSubSubText = showBalance ? formatBalance() : undefined;
        }
      } else {
        displayedSubText = formatNetwork(walletBlockchainDisplayName);
        displayedSubSubText = showBalance ? formatBalance() : undefined;
      }
    }
  } else if (showWalletAddress) {
    displayedSubText = formattedWalletAddress;
    displayedSubSubText = showBalance ? formatBalance() : undefined;
  } else if (showBalance) {
    displayedSubText = formatBalance();
  } else if (showTicker) {
    displayedSubText = resolvedToken.symbol;
  }
  return (
    <ListItem
      mainText={mainText || resolvedToken.symbol}
      subText={displayedSubText}
      subSubText={displayedSubSubText}
      subSubSubText={displayedSubSubSubText}
      subSubSubSubText={displayedSubSubSubSubText}
      mainTextSize={mainTextSize}
      mainTextColor={mainTextColor}
      subTextSize={subTextSize}
      subTextColor={subTextColor}
      subSubTextSize={subSubTextSize}
      subSubTextColor={subSubTextColor}
      subSubSubTextSize={subSubSubTextSize}
      subSubSubTextColor={subSubSubTextColor}
      subSubSubSubTextSize={subSubSubSubTextSize}
      subSubSubSubTextColor={subSubSubSubTextColor}
      leftIcon={
        leftIcon || (
          <TokenIcon
            token={resolvedToken}
            tokenSymbol={resolvedToken.symbol || ''}
            alt={resolvedToken.name || ''}
          />
        )
      }
      leftIconUrl={leftIconUrl}
      rightIconUrl={rightIconUrl}
      align={align}
      leftColor={leftColor}
      {...rest}
    />
  );
};
export default WalletTokenListItem;
